import { Badge } from '@/components/ui/badge'
import { nasHealthStatusBadgeVariant } from '@/lib/hyperBackup'
import {
  checkStatusLabel,
  checkStatusVariant,
  normaliseCheckStatus,
} from '@/lib/toolkitHealth'
import { cn } from '@/lib/utils'

function NasHealthChecksTable({ checks }) {
  const rows = checks ?? []
  if (rows.length === 0) return null

  return (
    <div className="rounded-md border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">Check</th>
            <th className="px-3 py-2 font-medium">Result</th>
            <th className="px-3 py-2 font-medium">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((check, index) => (
            <tr
              key={`${check.area || 'check'}-${index}`}
              className={cn(
                'border-b border-border last:border-0 align-top',
                ['WARN', 'FAIL'].includes(normaliseCheckStatus(check.status)) && 'bg-amber-500/5',
              )}
            >
              <td className="px-3 py-2 font-medium">{check.area || 'Check'}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                {check.status ? (
                  <Badge variant={checkStatusVariant(check.status)}>
                    {checkStatusLabel(check.status)}
                  </Badge>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-3 py-2 text-muted-foreground">{check.detail || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RecentAlertsList({ alerts }) {
  const recent = alerts?.recent ?? []
  if (recent.length === 0) return null

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Recent alert log entries (24h)
      </p>
      <ul className="space-y-2 text-sm">
        {recent.map((entry, index) => (
          <li
            key={`${entry.level}-${index}`}
            className="rounded-md border border-border bg-muted/20 px-3 py-2"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  entry.level === 'critical'
                    ? 'destructive'
                    : entry.level === 'warning'
                      ? 'warning'
                      : 'secondary'
                }
              >
                {entry.level === 'critical' ? 'Critical' : entry.level === 'warning' ? 'Warning' : entry.level}
              </Badge>
            </div>
            {entry.message && (
              <p className="mt-1 text-muted-foreground break-words">{entry.message}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function NasHealthSummary({ health, checks = [] }) {
  if (!health) {
    return (
      <p className="text-sm text-muted-foreground">
        No NAS health snapshot received yet.
      </p>
    )
  }

  const dsm = health.dsm
  const raid = health.raid
  const alerts = health.alerts
  const volumes = health.volumes ?? []

  return (
    <div className="space-y-4 text-sm">
      {checks.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Health checks
          </p>
          <NasHealthChecksTable checks={checks} />
        </div>
      )}

      {(alerts?.warning_count_24h > 0 || alerts?.critical_count_24h > 0) && (
        <RecentAlertsList alerts={alerts} />
      )}

      {volumes.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Volumes
          </p>
          {volumes.map((volume) => (
            <div key={volume.mount} className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">{volume.mount}</span>
              <div className="flex items-center gap-2">
                <span className="tabular-nums text-muted-foreground">{volume.used_percent ?? '—'}% used</span>
                {volume.status_label && (
                  <Badge variant={nasHealthStatusBadgeVariant(volume.status)}>
                    {volume.status_label}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <p className="text-xs text-muted-foreground">RAID / disks</p>
          <div className="mt-1 flex items-center gap-2">
            {raid?.status ? (
              <Badge variant={nasHealthStatusBadgeVariant(raid.status)}>
                {raid.status_label || raid.status}
              </Badge>
            ) : (
              <span>—</span>
            )}
          </div>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Alerts (24h)</p>
          <p className="mt-1">
            {alerts?.critical_count_24h ?? 0} critical, {alerts?.warning_count_24h ?? 0} warning
          </p>
        </div>
        <div className="sm:col-span-2">
          <p className="text-xs text-muted-foreground">DSM</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span>{dsm?.current_version || 'Unknown version'}</span>
            {dsm?.status_label && (
              <Badge variant={dsm.update_available ? 'warning' : 'success'}>
                {dsm.status_label}
                {dsm.available_version ? ` (${dsm.available_version})` : ''}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
